import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import CloseIcon from "@mui/icons-material/Close";
import TinyMCEEditorCommon from "../../components/common/TinyMCEEditorCommon";

// ─── Mock initial data (replace with API calls) ───────────────────────────────
const INITIAL_NEWS = [
  {
    id: 1,
    title: "Market Update: Nifty crosses 22,000",
    summary: "Nifty 50 index crossed the 22,000 mark for the first time...",
    content: "<p>Nifty 50 index crossed the 22,000 mark for the first time in Q1 2026...</p>",
    status: "published",
    createdAt: "2026-04-08",
  },
  {
    id: 2,
    title: "New Subscription Plans Live",
    summary: "We have launched three new subscription plans for premium users.",
    content: "<p>We have launched three new subscription plans...</p>",
    status: "draft",
    createdAt: "2026-04-09",
  },
];

const STATUS_COLORS = {
  published: "success",
  draft: "warning",
  archived: "default",
};

export default function NewsPage() {
  const editorRef = useRef(null);
  const viewEditorRef = useRef(null);

  const [newsList, setNewsList] = useState(INITIAL_NEWS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [crudType, setCrudType] = useState("create"); // "create" | "edit"
  const [activeNews, setActiveNews] = useState(null);
  const [form, setForm] = useState({ title: "", summary: "", status: "draft" });

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setCrudType("create");
    setActiveNews(null);
    setForm({ title: "", summary: "", status: "draft" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setCrudType("edit");
    setActiveNews(item);
    setForm({ title: item.title, summary: item.summary, status: item.status });
    setDialogOpen(true);
  };

  const openView = (item) => {
    setActiveNews(item);
    setViewDialogOpen(true);
  };

  const handleDelete = (id) => {
    setNewsList((prev) => prev.filter((n) => n.id !== id));
  };

  const handleSave = () => {
    const content = editorRef.current ? editorRef.current.getContent() : "";
    if (!form.title.trim()) return;

    if (crudType === "create") {
      const newItem = {
        id: Date.now(),
        title: form.title,
        summary: form.summary,
        content,
        status: form.status,
        createdAt: new Date().toISOString().split("T")[0],
      };
      setNewsList((prev) => [newItem, ...prev]);
    } else {
      setNewsList((prev) =>
        prev.map((n) =>
          n.id === activeNews.id
            ? { ...n, title: form.title, summary: form.summary, content, status: form.status }
            : n
        )
      );
    }
    setDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* ── Page Header ── */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <NewspaperIcon sx={{ color: "#fff", fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#fff">
              News Management
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              Create and manage news articles for users
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            background: "#016551",
            borderRadius: "8px",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
              boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
            },
          }}
        >
          Create News
        </Button>
      </Box>

      {/* ── News List ── */}
      <Card
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        }}
      >
        {/* Section header strip */}
        <Box
          sx={{
            background: "#04343a",
            px: 3,
            py: 1.5,
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="#fff">
            All Articles ({newsList.length})
          </Typography>
        </Box>

        <CardContent sx={{ p: 0 }}>
          {newsList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <NewspaperIcon sx={{ fontSize: 48, color: "#ccc", mb: 1 }} />
              <Typography color="text.secondary">No news articles yet. Create one!</Typography>
            </Box>
          ) : (
            newsList.map((item, idx) => (
              <React.Fragment key={item.id}>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    flexWrap: "wrap",
                    "&:hover": { background: "#f8fafc" },
                    transition: "background 0.15s",
                  }}
                >
                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {item.title}
                      </Typography>
                      <Chip
                        label={item.status}
                        size="small"
                        color={STATUS_COLORS[item.status] || "default"}
                        sx={{ textTransform: "capitalize", fontSize: 11 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {item.summary}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {item.createdAt}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => openView(item)}
                        sx={{ color: "#016551" }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(item)}
                        sx={{ color: "#04343a" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        sx={{ color: "#e53935" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {idx < newsList.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}
        </CardContent>
      </Card>

      {/* ── Create / Edit Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {crudType === "create" ? "Create News Article" : "Edit News Article"}
          </Typography>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: "24px !important", pb: 1, overflowY: "auto" }}>
          {/* ── Form Fields ── */}
          <Box
            sx={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              p: 2.5,
              mb: 2.5,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Title"
                  fullWidth
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: "#fff",
                      "&:hover fieldset": { borderColor: "#016551" },
                      "&.Mui-focused fieldset": { borderColor: "#016551" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#016551" },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Status"
                  select
                  fullWidth
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  size="small"
                  SelectProps={{ native: true }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: "#fff",
                      "&:hover fieldset": { borderColor: "#016551" },
                      "&.Mui-focused fieldset": { borderColor: "#016551" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#016551" },
                  }}
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Summary (short description)"
                  fullWidth
                  multiline
                  rows={2}
                  value={form.summary}
                  onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: "#fff",
                      "&:hover fieldset": { borderColor: "#016551" },
                      "&.Mui-focused fieldset": { borderColor: "#016551" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#016551" },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* ── Editor Section ── */}
          <Box
            sx={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              p: 2.5,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ mb: 1.5, color: "#04343a", fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              Article Content
            </Typography>
            <TinyMCEEditorCommon
              crudType={crudType}
              editorRef={editorRef}
              editable={true}
              initialContent={activeNews?.content || ""}
            />
          </Box>

          <Box sx={{ mt: 2.5, mb: 1, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDialogOpen(false)}
              sx={{ borderColor: "#04343a", color: "#04343a" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                },
              }}
            >
              {crudType === "create" ? "Publish Article" : "Save Changes"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── View Dialog ── */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", maxHeight: "90vh" } }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>{activeNews?.title}</Typography>
          <IconButton onClick={() => setViewDialogOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: "20px !important", pb: 2, overflowY: "auto" }}>
          {activeNews && (
            <TinyMCEEditorCommon
              crudType="view"
              editorRef={viewEditorRef}
              editable={false}
              initialContent={activeNews.content}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}